// One xterm.js instance per session. Lives outside React: panes borrow its element and give it
// back (hide, never dispose) so switching sessions keeps state and scrollback.

import { Terminal, type ITheme } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebglAddon } from '@xterm/addon-webgl';
import { Unicode11Addon } from '@xterm/addon-unicode11';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { ClipboardAddon } from '@xterm/addon-clipboard';
import { NO_OFFSET, encAck, encAttach, encDetach, encInput, encInputSeq, encResize, inputSeqToInput } from '../net/protocol';
import { conn } from '../net/connection';
import { telemetry } from '../net/telemetry';
import { NARROW_MQ, matches } from '../ui/mobile';
import { openLink } from '../ui/sheets';
import { applyMods, mods } from './modifiers';
import { isMac } from '../ui/keys';
import { ctrlV, filesFromPaste, sendFiles } from './drops';
import { escCancelsUpload } from './upload';
import { zoomSteps } from './zoom';

/** How long a session out of every pane stays attached (detachLater). */
const PARKED_MS = 15_000;

const MAC_LINE_KEYS: Record<string, string> = { Backspace: '\x15', Delete: '\x0b', ArrowLeft: '\x01', ArrowRight: '\x05' };
// ⌥ moves and deletes a word (Esc b / Esc f / Esc Del / Esc d), as the Mac and iPhone apps do
// (TerminalKit EditingKeys) and Ghostty: xterm.js 6 sends ⌥← as Esc [1;3D, which shells don't bind.
const MAC_WORD_KEYS: Record<string, string> = { Backspace: '\x1b\x7f', Delete: '\x1bd', ArrowLeft: '\x1bb', ArrowRight: '\x1bf' };

/** The default terminal font size: a notch smaller on phones so a 390 px screen still gets ~50 columns. */
export const termBaseSize = () => (matches(NARROW_MQ) ? 12 : 13);
/** A session's font size: the default and its own zoom (term/zoom.ts). */
export const termFontSize = (sid?: number) => termBaseSize() + (sid === undefined ? 0 : zoomSteps(sid));

/** The terminal's face: Geist Mono, the chrome's mono, at regular. What it has no glyph for comes from the
    system's monospaced font. */
export const FONT_FAMILY = '"Geist Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace';

/** A row is this many times the font size (19.5px at 13), as the Mac app's (TerminalKit FontSet). */
const ROW = 1.5;
let measure: CanvasRenderingContext2D | null | undefined;
/** xterm's `lineHeight` for that row. xterm multiplies the font's own height, not its size (most are about
    1.3 × their size, so a plain 1.5 made a row nearly twice the size): this is the row wanted over that height,
    both in the device pixels xterm cuts them to. Never under the font's own height. */
function rowLineHeight(px: number): number {
  const dpr = window.devicePixelRatio || 1;
  if (measure === undefined) measure = document.createElement('canvas').getContext('2d');
  let own = px * 1.2;
  if (measure) {
    measure.font = `${px}px ${FONT_FAMILY}`;
    const m = measure.measureText('W');
    const h = m.fontBoundingBoxAscent + m.fontBoundingBoxDescent;
    if (h > 0) own = h;
  }
  return Math.max(1, (Math.round(px * ROW * dpr) + 0.5) / Math.ceil(own * dpr));
}

// Tuned to the cmx palette: background = pane surface (--bg-1), cursor = the lime accent,
// selection = accent at low alpha. ANSI colours are desaturated a notch so agent UIs stay calm.
const DARK: ITheme = {
  background: '#0e1013',
  foreground: '#d8dbe0',
  cursor: '#b6ff3b',
  cursorAccent: '#0e1013',
  selectionBackground: 'rgba(182, 255, 59, 0.22)',
  selectionInactiveBackground: 'rgba(182, 255, 59, 0.12)',
  black: '#1b1f25',
  red: '#ef6b62',
  green: '#7fd48f',
  yellow: '#e6c170',
  blue: '#6fa8ff',
  magenta: '#c49bf5',
  cyan: '#5fd3d0',
  white: '#b5bac2',
  brightBlack: '#5b626c',
  brightRed: '#ff8f86',
  brightGreen: '#b6ff3b',
  brightYellow: '#f5d88f',
  brightBlue: '#9cc4ff',
  brightMagenta: '#dcc0ff',
  brightCyan: '#8ae8e4',
  brightWhite: '#f5f6f7',
};

const LIGHT: ITheme = {
  background: '#ffffff',
  foreground: '#25282d',
  cursor: '#3b6600',
  cursorAccent: '#ffffff',
  selectionBackground: 'rgba(120, 190, 20, 0.28)',
  selectionInactiveBackground: 'rgba(120, 190, 20, 0.16)',
  black: '#25282d',
  red: '#c4352d',
  green: '#2f7d32',
  yellow: '#8a6100',
  blue: '#2463c9',
  magenta: '#8045c4',
  cyan: '#137a80',
  white: '#6b717a',
  brightBlack: '#5c626b',
  brightRed: '#a3231c',
  brightGreen: '#3b6600',
  brightYellow: '#6e4c00',
  brightBlue: '#1a4fa8',
  brightMagenta: '#6a33a8',
  brightCyan: '#0e6166',
  brightWhite: '#9aa0a8',
};

/** Text stays readable whatever colours a program picks: a foreground too close to its cell's
 * background is darkened or lightened to WCAG AA. Programs made for a dark terminal (Claude
 * Code's dark theme) print white text that would vanish in the light theme, and the other way
 * round. The Mac and iPhone terminal use the same (TerminalKit Contrast.swift). */
const MIN_CONTRAST = 4.5;

const darkMq = matchMedia('(prefers-color-scheme: dark)');

/** The workspace's terminal in light: GitHub Light's terminal colours on white, the Mac's and iPhone's
 * (TerminalColors.lightWorkspace in Palette.swift). */
const WORKSPACE_LIGHT: ITheme = {
  background: '#ffffff',
  foreground: '#1f2328',
  cursor: '#1f2328',
  cursorAccent: '#ffffff',
  selectionBackground: 'rgb(21 112 239 / 0.2)',
  selectionInactiveBackground: 'rgb(21 112 239 / 0.12)',
  black: '#24292f',
  red: '#cf222e',
  green: '#116329',
  yellow: '#4d2d00',
  blue: '#0969da',
  magenta: '#8250df',
  cyan: '#1b7c83',
  white: '#6e7781',
  brightBlack: '#57606a',
  brightRed: '#a40e26',
  brightGreen: '#1a7f37',
  brightYellow: '#633c01',
  brightBlue: '#218bff',
  brightMagenta: '#a475f9',
  brightCyan: '#3192aa',
  brightWhite: '#8c959f',
};

/** The workspace's terminal in dark, read from the design tokens under html[data-theme='dark'] (the Mac's and
 * iPhone's darkWorkspace): --text on --surface (stone-950), the blue path, green marks, stone-450 for dim text. */
function workspaceDark(): ITheme {
  const cs = getComputedStyle(document.documentElement);
  const v = (name: string) => cs.getPropertyValue(name).trim();
  const surface = v('--surface');
  return {
    background: surface,
    foreground: v('--text'),
    cursor: v('--ink'),
    cursorAccent: surface,
    selectionBackground: v('--accent-ring'),
    selectionInactiveBackground: v('--run-ring'),
    black: v('--stone-800'),
    red: v('--fault-text'),
    green: v('--live'),
    yellow: v('--attn-text'),
    blue: v('--accent'),
    magenta: v('--stone-400'),
    cyan: v('--blue-200'),
    white: v('--muted'),
    brightBlack: v('--faint'),
    brightRed: v('--red-400'),
    brightGreen: v('--live-text'),
    brightYellow: v('--amber-400'),
    brightBlue: v('--blue-300'),
    brightMagenta: v('--stone-300'),
    brightCyan: v('--blue-300'),
    brightWhite: v('--ink'),
  };
}

const themeIsDark = () => (document.documentElement.dataset.theme ? document.documentElement.dataset.theme === 'dark' : darkMq.matches);
/** On the workspace's routes its own (the app's Appearance); elsewhere the terminal follows the computer's colour scheme. */
export const currentTheme = () => (document.documentElement.dataset.theme ? (themeIsDark() ? workspaceDark() : WORKSPACE_LIGHT) : darkMq.matches ? DARK : LIGHT);
/** The terminal's light or dark, as Ghostty says it (CSI ?997;1n dark, ?997;2n light). */
const schemeReport = (dark: boolean) => `\x1b[?997;${dark ? 1 : 2}n`;

const ACK_BYTES = 16 * 1024;
/** Before a skip-ahead snapshot: end any sequence cut off mid-way (CAN), leave the alternate
 * screen, undo modes the app may have set (soft reset, mouse, bracketed paste), clear the
 * screen but not the scrollback. */
const RESYNC_PREFIX = '\x18\x1b[?1049l\x1b[!p\x1b[?1000l\x1b[?1002l\x1b[?1003l\x1b[?1006l\x1b[?2004l\x1b[H\x1b[2J';
const ACK_MS = 20;
/** A snapshot asked for and not here after this long is asked for again (tick): the answer may
 * never come (lost with a stream the machine reopened), and nothing waits on it for good. */
const SNAP_REASK_MS = 1000;
const ECHO_MAX_AGE = 2000;
/** Unacknowledged input kept for resending after a reconnect (beyond this, input typed while
 * offline is dropped as before). */
const MAX_UNACKED_BYTES = 4 << 20;
const utf8 = new TextEncoder();

/** A random input stream id (u64) for this terminal instance. */
function newInputStream(): bigint {
  const b = new BigUint64Array(1);
  crypto.getRandomValues(b);
  return b[0] | 1n;
}

interface PendingEcho {
  bytes: Uint8Array;
  t0: number;
  /** When the input frame was handed to the transport. */
  sent: number;
}

/** Find `needle` in `hay` starting at `from`. Needles are 1-4 bytes. */
function indexOfBytes(hay: Uint8Array, needle: Uint8Array, from: number): number {
  const n0 = needle[0];
  const nl = needle.length;
  outer: for (let i = hay.indexOf(n0, from); i !== -1 && i <= hay.length - nl; i = hay.indexOf(n0, i + 1)) {
    for (let j = 1; j < nl; j++) if (hay[i + j] !== needle[j]) continue outer;
    return i;
  }
  return -1;
}

/** Hidden holder for terminal elements not currently shown in a pane. */
let parking: HTMLDivElement | null = null;
function park(): HTMLDivElement {
  if (!parking) {
    parking = document.createElement('div');
    parking.className = 'term-parking';
    parking.setAttribute('aria-hidden', 'true');
    document.body.appendChild(parking);
  }
  return parking;
}

/** Diagnostics counters (exposed via window.__cmx). */
export const termCounters = { gaps: 0, duplicates: 0, snapshots: 0 };

export class SessionTerm {
  readonly term: Terminal;
  readonly el: HTMLDivElement;
  private fit = new FitAddon();
  private webgl: WebglAddon | null = null;
  private opened = false;

  /** Next expected stream offset (bytes received and handed to xterm). */
  offset: bigint | null = null;
  private rendered = 0n;
  private acked = 0n;
  private ackTimer: ReturnType<typeof setTimeout> | null = null;

  attached = false;
  /** Leaving a session that's in no pane any more (detachLater). */
  private detachTimer: ReturnType<typeof setTimeout> | null = null;
  exitCode: number | null = null;
  private lastSent: { cols: number; rows: number } | null = null;
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;
  private echo: PendingEcho[] = [];
  private resyncPending = false;
  /** When a snapshot was last asked for (performance.now), until one comes; null otherwise. */
  private snapAskedAt: number | null = null;
  private lastKeyTs = 0;
  private disposed = false;
  private listeners = new Set<() => void>();

  // Durable input (protocol v2): every keystroke is numbered and kept until INPUT_ACK.
  private readonly inputStream = newInputStream();
  private inputSeq = 0;
  private unacked: Uint8Array[] = []; // INPUT_SEQ frames, in seq order
  private unackedSeqs: number[] = [];
  private unackedBytes = 0;

  // Input held while a file upload or clipboard copy for this session is pending (drops.ts):
  // typed keys wait and follow the pasted paths / Ctrl+V, in order; Esc alone stops an upload.
  private holds = 0;
  private heldInput: (string | Uint8Array)[] = [];
  private bypassHold = false;

  private dark = themeIsDark();
  /** The program asked to be told when light and dark flip (mode 2031). */
  private reportsScheme = false;

  constructor(readonly sid: number) {
    this.el = document.createElement('div');
    this.el.className = 'term-host';
    this.el.dataset.session = String(sid);
    park().appendChild(this.el);
    this.term = new Terminal({
      allowProposedApi: true,
      fontFamily: FONT_FAMILY,
      fontSize: termFontSize(sid),
      lineHeight: rowLineHeight(termFontSize(sid)),
      fontWeightBold: 700, // bold is 700, as on the Mac
      cursorBlink: true,
      scrollback: 10_000,
      macOptionIsMeta: false,
      macOptionClickForcesSelection: true,
      theme: currentTheme(),
      minimumContrastRatio: MIN_CONTRAST,
      smoothScrollDuration: 0,
      rescaleOverlappingGlyphs: true,
    });
    this.term.loadAddon(this.fit);
    this.term.loadAddon(new Unicode11Addon());
    this.term.unicode.activeVersion = '11';
    // Plain URLs and OSC 8 hyperlinks (Claude Code prints its sign-in link as one) both go
    // through openLink: a new tab on desktop, an Open / Copy menu on touch devices.
    // From this session: where a program in it opening the link would (a sign-in calling back to the machine opens
    // in the machine's browser, which its callback reaches).
    this.term.loadAddon(new WebLinksAddon((_e, uri) => openLink(uri, this.sid)));
    this.term.options.linkHandler = { activate: (_e, uri) => openLink(uri, this.sid), allowNonHttpProtocols: false };
    this.term.loadAddon(new ClipboardAddon());

    // Record the real keydown time for echo latency (Event.timeStamp shares performance.now's origin).
    this.term.attachCustomKeyEventHandler((ev) => {
      if (ev.type === 'keydown') {
        this.lastKeyTs = ev.timeStamp;
        // Ctrl+V (the Ctrl key itself, not Cmd): programs on the machine read the clipboard on
        // it, so this computer's clipboard is copied there first (drops.ts).
        if (ev.ctrlKey && !ev.metaKey && !ev.altKey && !ev.shiftKey && (ev.key === 'v' || ev.key === 'V') && this.exitCode === null) {
          ev.preventDefault();
          ctrlV(this);
          return false;
        }
        // Mac line editing, as in iTerm2 and Ghostty: ⌘⌫ / ⌘⌦ delete to the start / end of
        // the line (Ctrl+U / Ctrl+K), ⌘← / ⌘→ go to its start / end (Ctrl+A / Ctrl+E).
        // xterm.js turns no ⌘ key into input (⌘⌦ even arrives as a stray "~").
        if (ev.metaKey && !ev.ctrlKey && !ev.altKey && !ev.shiftKey && MAC_LINE_KEYS[ev.key] && isMac()) {
          ev.preventDefault();
          this.onData(MAC_LINE_KEYS[ev.key]);
          return false;
        }
        if (ev.altKey && !ev.ctrlKey && !ev.metaKey && !ev.shiftKey && MAC_WORD_KEYS[ev.key] && isMac()) {
          ev.preventDefault();
          this.onData(MAC_WORD_KEYS[ev.key]);
          return false;
        }
      }
      return true;
    });
    // Programs that follow the terminal's light or dark (Claude Code's "Auto (match terminal)"): asked (CSI ?996n),
    // the terminal says which; asked to be told (mode 2031), a flip is reported. xterm.js answers OSC 10 / 11 itself;
    // the Mac's and iPhone's terminal (Ghostty's) does all three.
    this.term.parser.registerCsiHandler({ prefix: '?', final: 'n' }, (p) => {
      if (p[0] !== 996) return false;
      this.reply(schemeReport(this.dark));
      return true;
    });
    const mode2031 = (on: boolean) => (p: (number | number[])[]) => {
      if (p.includes(2031)) this.reportsScheme = on;
      return false; // xterm.js still sets the rest
    };
    this.term.parser.registerCsiHandler({ prefix: '?', final: 'h' }, mode2031(true));
    this.term.parser.registerCsiHandler({ prefix: '?', final: 'l' }, mode2031(false));
    this.term.onData(this.onData);
    this.term.onBinary((d) => {
      const b = new Uint8Array(d.length);
      for (let i = 0; i < d.length; i++) b[i] = d.charCodeAt(i) & 0xff;
      if (this.holds && !this.bypassHold) {
        this.heldInput.push(b);
        return;
      }
      this.sendInput(b);
    });
    // A paste of files with no text (a copied screenshot or file): upload, paste the machine
    // paths. Capture phase on the host runs before xterm's own paste handler.
    this.el.addEventListener(
      'paste',
      (e) => {
        const files = filesFromPaste(e);
        if (!files || this.exitCode !== null) return;
        e.preventDefault();
        e.stopPropagation();
        sendFiles(this, files, { alsoClipboard: true });
      },
      true,
    );
    // A click the program takes (Claude Code takes the mouse) lets go of the selection, as in Ghostty and iTerm (and
    // the Mac and Windows apps); xterm kept it until the next Option-click (Shift-click off a Mac), which still selects.
    this.el.addEventListener(
      'mousedown',
      (e) => {
        if (e.button !== 0 || this.term.modes.mouseTrackingMode === 'none' || !this.term.hasSelection()) return;
        if (isMac() ? e.altKey : e.shiftKey) return;
        this.term.clearSelection();
      },
      true,
    );
  }

  /**
   * Send input now; on a v2 connection also keep it (numbered) until the agent acknowledges
   * it, so a connection that dies with the frame in flight loses nothing: it is resent after
   * the reconnect and the agent drops duplicates. The bookkeeping is a push onto an array.
   */
  private sendInput(data: string | Uint8Array) {
    const mode = conn.inputMode;
    if (mode === 'v1') {
      conn.send(encInput(this.sid, data));
      conn.expectReply();
      return;
    }
    const frame = encInputSeq(this.sid, this.inputStream, ++this.inputSeq, data);
    if (this.unackedBytes + frame.length <= MAX_UNACKED_BYTES) {
      this.unacked.push(frame);
      this.unackedSeqs.push(this.inputSeq);
      this.unackedBytes += frame.length;
    }
    // While the connection's version is unknown (or there is none) the frame waits for resendInput().
    if (mode === 'v2') {
      conn.send(frame);
      conn.expectReply();
    }
  }

  /** The connection's version is known: resend what the agent has not acknowledged, in order. */
  resendInput() {
    if (!this.unacked.length) return;
    if (conn.inputMode === 'v2') {
      for (const f of this.unacked) conn.send(f);
    } else if (conn.inputMode === 'v1') {
      for (const f of this.unacked) conn.send(inputSeqToInput(f));
      this.dropUnacked();
    }
  }

  onInputAck(stream: bigint, seq: number) {
    if (stream !== this.inputStream) return;
    let i = 0;
    while (i < this.unackedSeqs.length && this.unackedSeqs[i] <= seq) this.unackedBytes -= this.unacked[i++].length;
    if (i) {
      this.unacked.splice(0, i);
      this.unackedSeqs.splice(0, i);
    }
  }

  /** Input not yet acknowledged by the agent (tests). */
  get pendingInput(): number {
    return this.unacked.length;
  }

  private dropUnacked() {
    this.unacked = [];
    this.unackedSeqs = [];
    this.unackedBytes = 0;
  }

  // --- input path (keep it tiny) ----------------------------------------------------------

  private onData = (d: string) => {
    if (this.exitCode !== null) return;
    if (mods.ctrl) d = applyMods(d); // sticky Ctrl from the mobile key bar (one boolean check otherwise)
    if (this.holds && !this.bypassHold) {
      if (!escCancelsUpload(this.sid, d)) this.heldInput.push(d);
      return;
    }
    this.sendInput(d);
    // Echo tracking for single printable characters.
    if (d.length <= 2 && !document.hidden) {
      const cp = d.codePointAt(0)!;
      if (cp >= 0x20 && cp !== 0x7f && String.fromCodePoint(cp).length === d.length) {
        const now = performance.now();
        const t0 = now - this.lastKeyTs < 50 ? this.lastKeyTs : now;
        if (this.echo.length >= 64) this.echo.shift();
        this.echo.push({ bytes: utf8.encode(d), t0, sent: now });
      }
    }
  };

  // --- output path ------------------------------------------------------------------------

  private paintWaiters = new Set<() => void>();

  /**
   * One-shot: call `f` on the frame after this terminal next draws output (the written→frame
   * stages onOutput/onSnapshot already time). Used for action timings (net/telemetry.ts ops).
   */
  afterPaint(f: () => void): () => void {
    this.paintWaiters.add(f);
    return () => this.paintWaiters.delete(f);
  }

  private painted() {
    if (!this.paintWaiters.size) return;
    const waiters = [...this.paintWaiters];
    this.paintWaiters.clear();
    requestAnimationFrame(() => waiters.forEach((f) => f()));
  }

  /** The agent resumed a paused stream: a snapshot next skips ahead and keeps our scrollback. */
  expectResync() {
    this.resyncPending = true;
  }

  onOutput(offset: bigint, data: Uint8Array) {
    this.resyncPending = false; // the gap was replayed: nothing skipped
    if (this.offset === null) {
      // Output before any snapshot: accept as the start of our view.
      this.offset = offset;
      this.rendered = this.acked = offset;
    }
    const end = offset + BigInt(data.length);
    if (end <= this.offset) {
      termCounters.duplicates++; // already have it
      return;
    }
    if (offset < this.offset) {
      termCounters.duplicates++;
      data = data.subarray(Number(this.offset - offset));
    } else if (offset > this.offset) {
      termCounters.gaps++;
      console.info(`cmx: session ${this.sid} output gap ${this.offset}→${offset}`);
    }
    this.offset = end;

    const matched = this.echo.length ? this.matchEcho(data) : null;
    const arrived = conn.rxAt;
    this.term.write(data, () => {
      this.rendered = end;
      this.maybeAck();
      this.painted();
      if (matched) {
        const written = performance.now();
        requestAnimationFrame((vsync) => {
          const now = performance.now();
          for (const m of matched) {
            telemetry.recordEcho(now - m.t0);
            telemetry.recordEchoTrace({ key: m.t0, sent: m.sent, arrived, written, frame: now, vsync });
          }
        });
      }
    });
  }

  /**
   * Keys whose echo arrived in this frame, in order. Each key is looked for on its own: a key
   * the frame doesn't carry stays pending without holding the keys typed after it (a miss used
   * to stop the scan, so one key a program consumed without echoing — vim's j, a menu's q —
   * held every key behind it and their real echoes were credited to a much later frame).
   */
  private matchEcho(data: Uint8Array): PendingEcho[] | null {
    const now = performance.now();
    while (this.echo.length && now - this.echo[0].t0 > ECHO_MAX_AGE) {
      this.echo.shift();
      telemetry.recordUnmatched();
    }
    const matched: PendingEcho[] = [];
    const kept: PendingEcho[] = [];
    let pos = 0;
    for (const e of this.echo) {
      const i = indexOfBytes(data, e.bytes, pos);
      if (i < 0) {
        kept.push(e); // not in this frame: it waits, and blocks nobody
        continue;
      }
      pos = i + e.bytes.length;
      matched.push(e);
    }
    this.echo = kept;
    return matched.length ? matched : null;
  }

  onSnapshot(offset: bigint, cols: number, rows: number, data: Uint8Array) {
    termCounters.snapshots++;
    this.snapAskedAt = null;
    if (this.resyncPending && this.offset !== null) {
      // Skipping ahead after a flood: repaint the screen in place, keeping scrollback (the
      // skipped output never reaches it), the way the CLI does.
      this.resyncPending = false;
      if (cols > 0 && rows > 0 && (cols !== this.term.cols || rows !== this.term.rows)) this.term.resize(cols, rows);
      this.offset = offset;
      this.term.write(RESYNC_PREFIX);
      this.term.write(data, () => {
        this.rendered = offset;
        this.acked = offset < this.acked ? offset : this.acked;
        this.maybeAck(true);
        this.painted();
      });
      return;
    }
    this.term.reset();
    this.reportsScheme = false; // the snapshot sets the program's modes again
    if (cols > 0 && rows > 0 && (cols !== this.term.cols || rows !== this.term.rows)) {
      this.term.resize(cols, rows);
    }
    this.lastSent = cols > 0 && rows > 0 ? { cols, rows } : this.lastSent;
    this.offset = offset;
    this.term.write(data, () => {
      this.rendered = offset;
      this.acked = offset < this.acked ? offset : this.acked;
      this.maybeAck(true);
      this.painted();
      // The server PTY is now at the snapshot size; re-fit to our pane (sends RESIZE if needed).
      this.refit();
    });
  }

  onExited(code: number) {
    this.exitCode = code;
    this.dropUnacked();
    this.emit();
  }

  private maybeAck(force = false) {
    if (!conn.connected) return;
    if (force || this.rendered - this.acked >= ACK_BYTES) {
      this.sendAck();
      return;
    }
    if (!this.ackTimer && this.rendered !== this.acked) {
      this.ackTimer = setTimeout(() => {
        this.ackTimer = null;
        this.sendAck();
      }, ACK_MS);
    }
  }

  private sendAck() {
    if (this.ackTimer) {
      clearTimeout(this.ackTimer);
      this.ackTimer = null;
    }
    if (conn.send(encAck(this.sid, this.rendered))) this.acked = this.rendered;
  }

  // --- attach / connection ----------------------------------------------------------------

  /** (Re)attach on the current connection, resuming from our last offset; with no screen yet,
   *  or a snapshot asked for and not here, for a snapshot (asked again by tick until it comes). */
  attach() {
    if (this.disposed) return;
    this.attached = true;
    if (!conn.connected) return;
    const from = this.snapAskedAt !== null ? NO_OFFSET : (this.offset ?? NO_OFFSET);
    if (from === NO_OFFSET) this.snapAskedAt = performance.now();
    conn.send(encAttach(this.sid, from));
    this.lastSent = null;
    this.refit(true);
    this.saidViewing = true; // an attach starts out in sight (agent viewing.go)
    this.sayViewing();
  }

  /**
   * Every 100 ms (manager): each layer decides from what it sees, never waiting on an answer
   * that may not come. A snapshot asked for over SNAP_REASK_MS ago is asked for again; output
   * drawn and not acknowledged (an ack that couldn't go out) goes out now.
   */
  tick(now: number) {
    if (this.disposed || !this.attached || !conn.connected) return;
    if (this.snapAskedAt !== null && now - this.snapAskedAt >= SNAP_REASK_MS) {
      console.info(`cmx: session ${this.sid} no snapshot ${Math.round(now - this.snapAskedAt)} ms after asking: asks again`);
      this.attach();
    }
    if (this.rendered !== this.acked && !this.ackTimer) this.sendAck();
  }

  /** The page is shown again: a hidden page draws no frames and its timers run late, so the
   *  pane draws its whole screen now and acknowledges what it has, current at once. */
  shown() {
    if (this.disposed || !this.opened || !this.inSight) return;
    this.refit();
    this.term.refresh(0, this.term.rows - 1);
    if (this.attached && this.rendered !== this.acked) this.maybeAck(true);
  }

  /** In sight: in a pane, on a page that's shown. */
  private get inSight(): boolean {
    return this.el.isConnected && this.el.parentElement !== parking && document.visibilityState === 'visible';
  }

  /** In a pane and shown here (not parked): the session is on screen. */
  get onScreen(): boolean {
    return this.inSight;
  }

  private saidViewing: boolean | null = null;

  /** Tells the machine whether this session is in sight here, when that changed (agent
   *  viewing.go: what someone saw, and whether the phone hears that it needs you). */
  sayViewing() {
    if (this.disposed || !this.attached || !conn.connected) return;
    const on = this.inSight;
    if (on === this.saidViewing) return;
    this.saidViewing = on;
    void conn.control('viewing', { session: this.sid, on }).catch(() => {});
  }

  // --- layout -----------------------------------------------------------------------------

  mount(parent: HTMLElement) {
    if (this.detachTimer) clearTimeout(this.detachTimer);
    this.detachTimer = null;
    if (this.el.parentElement !== parent) parent.appendChild(this.el);
    if (!this.opened) {
      this.opened = true;
      this.term.open(this.el);
      this.loadWebgl();
      // Mobile keyboards: no auto-capitalisation, autocorrect, suggestions or spellcheck.
      const ta = this.term.textarea;
      if (ta) {
        ta.setAttribute('autocapitalize', 'off');
        ta.setAttribute('autocorrect', 'off');
        ta.setAttribute('autocomplete', 'off');
        ta.spellcheck = false;
      }
    }
    this.refit(true);
    if (!this.attached) this.attach();
    this.sayViewing();
  }

  unmount(parent: HTMLElement) {
    if (this.el.parentElement !== parent) return;
    park().appendChild(this.el);
    this.sayViewing();
    this.detachLater();
  }

  /**
   * A session in no pane is left after PARKED_MS: the machine then knows nobody here has it
   * open, so what it prints next shows as Done in the lists, and it can close on its own (agent
   * lifecycle.go). Coming back within it is instant; later, it attaches again from where it was.
   */
  private detachLater() {
    if (this.detachTimer) clearTimeout(this.detachTimer);
    this.detachTimer = setTimeout(() => {
      this.detachTimer = null;
      if (this.disposed || !this.attached || this.el.parentElement !== park()) return;
      this.sendAck();
      this.attached = false;
      conn.send(encDetach(this.sid));
    }, PARKED_MS);
  }

  focus() {
    this.term.focus();
  }

  get focused(): boolean {
    const ta = this.term.textarea;
    return !!ta && document.activeElement === ta;
  }

  /** Blur the input (phones: lets a following focus() reopen the on-screen keyboard). */
  blur() {
    this.term.blur();
  }

  /**
   * Send input as if typed: goes through xterm's onData (same path as a keystroke), so it also
   * scrolls to the bottom and picks up an armed key-bar modifier.
   */
  input(data: string) {
    this.term.input(data, true);
  }

  /** Paste text (bracketed paste when the app asked for it). */
  paste(text: string) {
    this.term.paste(text);
  }

  /** Hold typed input until release() (an upload or clipboard copy is pending). */
  hold() {
    this.holds++;
  }

  /**
   * End a hold: first paste `paste` (bracketed when the app asked for it) or send `input`, then
   * the input typed meanwhile, in order.
   */
  release(first: { paste?: string; input?: string } = {}) {
    if (this.holds === 0) return;
    this.bypassHold = true;
    try {
      if (first.paste) this.term.paste(first.paste);
      if (first.input) this.sendInput(first.input);
    } finally {
      this.bypassHold = false;
    }
    if (--this.holds > 0) return;
    const held = this.heldInput;
    this.heldInput = [];
    for (const d of held) this.sendInput(d);
  }

  /** Input is held for a pending upload (tests). */
  get holding(): boolean {
    return this.holds > 0;
  }

  get applicationCursorKeys(): boolean {
    return this.term.modes.applicationCursorKeysMode;
  }

  /** Also when only the display's density changed: a row is cut to whole device pixels. */
  setFontSize(px: number) {
    const lineHeight = rowLineHeight(px);
    if (this.term.options.fontSize === px && this.term.options.lineHeight === lineHeight) return;
    this.term.options.fontSize = px;
    this.term.options.lineHeight = lineHeight;
    this.refit();
  }

  /** Height of one terminal row in CSS pixels (for touch scrolling). */
  get rowHeight(): number {
    const screen = this.term.element?.querySelector<HTMLElement>('.xterm-screen');
    const h = screen?.clientHeight ?? 0;
    return h > 0 && this.term.rows > 0 ? h / this.term.rows : 16;
  }

  /** True when a touch drag scrolls our own scrollback (not an app that handles the wheel itself). */
  get scrollsLocally(): boolean {
    return this.term.modes.mouseTrackingMode === 'none' && this.term.buffer.active.type === 'normal';
  }

  /**
   * Scroll by whole rows from a touch drag, mirroring what a mouse wheel does in xterm:
   * apps with mouse tracking get wheel events, the alternate screen (less, vim, htop) gets
   * arrow keys, and the normal screen scrolls its scrollback.
   */
  touchScroll(lines: number, clientX: number, clientY: number) {
    if (!lines) return;
    const t = this.term;
    if (t.modes.mouseTrackingMode !== 'none') {
      const target = t.element?.querySelector('.xterm-screen');
      const n = Math.min(Math.abs(lines), 10);
      for (let i = 0; i < n; i++) {
        target?.dispatchEvent(
          new WheelEvent('wheel', { deltaY: Math.sign(lines), deltaMode: WheelEvent.DOM_DELTA_LINE, clientX, clientY, bubbles: true, cancelable: true }),
        );
      }
    } else if (t.buffer.active.type === 'alternate') {
      if (this.exitCode !== null) return;
      const seq = lines > 0 ? 'B' : 'A';
      const one = (t.modes.applicationCursorKeysMode ? '\x1bO' : '\x1b[') + seq;
      this.sendInput(one.repeat(Math.min(Math.abs(lines), 10)));
    } else {
      t.scrollLines(lines);
    }
  }

  private loadWebgl() {
    try {
      const gl = new WebglAddon();
      gl.onContextLoss(() => {
        gl.dispose();
        this.webgl = null; // falls back to the DOM renderer
      });
      this.term.loadAddon(gl);
      this.webgl = gl;
    } catch (e) {
      console.info('cmx: WebGL renderer unavailable, using DOM renderer', e);
      this.webgl = null;
    }
  }

  get renderer(): 'webgl' | 'dom' {
    return this.webgl ? 'webgl' : 'dom';
  }

  /** Fit to the container now; send RESIZE (debounced 50 ms) if the grid changed. */
  refit(immediate = false) {
    if (!this.opened || !this.el.isConnected || this.el.parentElement === parking) return;
    const dims = this.fit.proposeDimensions();
    if (!dims || !Number.isFinite(dims.cols) || !Number.isFinite(dims.rows) || dims.cols < 2 || dims.rows < 1) return;
    if (dims.cols !== this.term.cols || dims.rows !== this.term.rows) this.term.resize(dims.cols, dims.rows);
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    const send = () => {
      this.resizeTimer = null;
      const { cols, rows } = this.term;
      if (this.lastSent && this.lastSent.cols === cols && this.lastSent.rows === rows) return;
      if (conn.send(encResize(this.sid, cols, rows))) this.lastSent = { cols, rows };
    };
    if (immediate) send();
    else this.resizeTimer = setTimeout(send, 50);
  }

  setTheme(t: ITheme) {
    this.term.options.theme = t;
    const dark = themeIsDark();
    if (dark !== this.dark && this.reportsScheme) this.reply(schemeReport(dark));
    this.dark = dark;
  }

  /** The terminal's answer to a program's question: straight to the session, never held or modified. */
  private reply(d: string) {
    if (this.exitCode === null) this.sendInput(d);
  }

  /** Plain-text buffer contents (select-text sheet, tests, debugging). */
  text(): string {
    const b = this.term.buffer.active;
    const out: string[] = [];
    for (let i = 0; i < b.length; i++) {
      const line = b.getLine(i);
      const s = line?.translateToString(true) ?? '';
      // Soft-wrapped rows continue the previous logical line.
      if (line?.isWrapped && out.length) out[out.length - 1] += s;
      else out.push(s);
    }
    while (out.length && out[out.length - 1] === '') out.pop();
    return out.join('\n');
  }

  subscribe(fn: () => void) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    this.listeners.forEach((l) => l());
  }

  dispose() {
    this.disposed = true;
    if (this.detachTimer) clearTimeout(this.detachTimer);
    if (this.ackTimer) clearTimeout(this.ackTimer);
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.term.dispose();
    this.el.remove();
    this.listeners.clear();
    this.paintWaiters.clear();
  }
}
